import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "~/components/Button";
import { PlanOptionsInput } from "~/components/PlanOptionsInput";
import { SelectInput } from "~/components/SelectInput";
import { MaskedInput, TextInput } from "~/components/TextInput";
import {
	type CreateDiscountDTO,
	createDiscountSchema,
} from "~/modules/sales/schemas";
import { createDiscount } from "~/modules/sales/services";
import { discountTypeOptions } from "~/modules/sales/variables";

type Props = {
	onSuccess?: () => void;
};

export const CreateDiscountForm: React.FC<Props> = ({ onSuccess }) => {
	const {
		control,
		handleSubmit,
		watch,
		formState: { errors, isSubmitting },
	} = useForm<CreateDiscountDTO>({
		defaultValues: {
			cycles: undefined,
		},
		resolver: zodResolver(createDiscountSchema),
	});

	const onSubmit = useCallback(
		async (payload: CreateDiscountDTO) => {
			// return
			const discount = await createDiscount(payload);
			if (discount) {
				onSuccess && onSuccess();
			}
		},
		[onSuccess],
	);

	return (
		<form className="grid grid-cols-1 gap-4" onSubmit={handleSubmit(onSubmit)}>
			<Controller
				control={control}
				name="planId"
				render={({ field }) => (
					<PlanOptionsInput
						label="Plano"
						hint="Para tornar este cupom somente aplicável em um determinado
              plano, selecione o plano abaixo. Deixe em branco, para que este
              cupom seja aplicável em qualquer plano"
						placeholder="Selecione um curso"
						error={errors.planId?.message}
						onChange={field.onChange}
						value={field.value}
					/>
				)}
			/>

			<Controller
				control={control}
				name="code"
				defaultValue=""
				render={({ field }) => (
					<TextInput
						label="Código do Cupom"
						placeholder="Digite o código do cupom"
						error={errors.code?.message}
						value={field.value}
						onChange={(e) =>
							field.onChange(e.target.value.trim().toUpperCase())
						}
					/>
				)}
			/>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2 bg-gpa-blue-50 rounded-md">
				<Controller
					control={control}
					name="type"
					render={({ field }) => (
						<SelectInput
							label="Tipo de Desconto"
							placeholder="Selecione o tipo de desconto"
							error={errors.type?.message}
							options={discountTypeOptions}
							value={field.value}
							onChange={field.onChange}
						/>
					)}
				/>
				<Controller
					control={control}
					name="value"
					defaultValue={0}
					render={({ field }) =>
						watch("type") === "percentage" ? (
							<MaskedInput
								key="percentage-input"
								label="Valor do Desconto"
								placeholder="Digite o valor do desconto"
								error={errors.value?.message}
								mask="integer"
								value={field.value}
								onChange={field.onChange}
							/>
						) : (
							<MaskedInput
								key="flat-input"
								label="Valor do Desconto"
								placeholder="Digite o valor do desconto"
								error={errors.value?.message}
								mask="money"
								value={field.value}
								onChange={field.onChange}
							/>
						)
					}
				/>
			</div>
			<div className="grid grid-cols-1 gap-4">
				<Controller
					control={control}
					name="expiresAt"
					render={({ field }) => (
						<MaskedInput
							mask="date"
							placeholder="dd/mm/aaaa"
							inputMode="numeric"
							label="Data de expiração"
							value={field.value}
							onChange={field.onChange}
							error={errors.expiresAt?.message}
						/>
					)}
				/>
				<Controller
					control={control}
					name="limit"
					render={({ field }) => (
						<MaskedInput
							label="Limites de uso"
							hint="O número de vezes que esse cupom pode ser usado. Exemplo:
                Se o valor for 1, somente uma compra poderá ser feita com esse
                cupom. Se o valor for vazio ou 0, então não há limites de uso
                para este cupom."
							mask="integer"
							value={field.value || ""}
							onChange={field.onChange}
							error={errors.limit?.message}
						/>
					)}
				/>
				<Controller
					control={control}
					name="cycles"
					render={({ field }) => (
						<MaskedInput
							label="Número de cíclos"
							hint="O número de vezes em que esse cupom será aplicado. Exemplo:
                um valor de 2 significa que o valor com desconto virá nas
                próximas duas cobranças. Se valor deste campo for vazio ou 0,
                então o desconto será aplicado para todas as cobranças"
							mask="integer"
							value={field.value || ""}
							onChange={field.onChange}
							error={errors.cycles?.message}
						/>
					)}
				/>
			</div>

			{/* <div className='flex justify-end mb-5'>
        <Checkbox label='Exclusivo para alunos?' {...register('private')} />
      </div> */}

			<Button loading={isSubmitting}>Criar Desconto</Button>
		</form>
	);
};
